// Client for the licence hub: better-auth at /api/auth, the portal API at /api/portal/v1.
// The session token lives in sessionStorage (tab lifetime), never localStorage.

const TOKEN_KEY = "polygon_devportal_session";

const HUB_BASE = (import.meta.env.VITE_HUB_API_BASE || "").replace(/\/+$/, "");
export const AUTH_BASE = `${HUB_BASE}/api/auth`;
export const PORTAL_BASE = `${HUB_BASE}/api/portal/v1`;

let memoryToken = null;

export function getToken() {
  try {
    return sessionStorage.getItem(TOKEN_KEY) || memoryToken;
  } catch (e) {
    return memoryToken;
  }
}

export function setToken(token) {
  memoryToken = token || null;
  try {
    if (token) sessionStorage.setItem(TOKEN_KEY, token);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch (e) {
    // Storage blocked: the in-memory copy carries the tab.
  }
  for (const fn of tokenListeners) fn(memoryToken);
}

const tokenListeners = new Set();
export function onTokenChange(fn) {
  tokenListeners.add(fn);
  return () => tokenListeners.delete(fn);
}

export class ApiError extends Error {
  constructor(body, status) {
    super((body && (body.message || body.detail || body.title)) || "Request failed");
    this.name = "ApiError";
    this.status = status;
    this.code = (body && body.code) || (status ? `HTTP_${status}` : "NETWORK");
    this.errors = (body && Array.isArray(body.errors) && body.errors) || null;
    this.body = body || null;
  }
}

// The old gate wrapped everything in { data }; accept either shape.
function unwrap(body) {
  if (body && typeof body === "object" && !Array.isArray(body) && "data" in body) return body.data;
  return body;
}

async function call(url, { method = "GET", body, cookies = false } = {}) {
  const headers = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  let res;
  try {
    res = await fetch(url, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      // Only the sign-in/2FA/sign-out calls need cookies: better-auth keeps the pending
      // second factor in the `plscks.two_factor` cookie, not in the bearer token.
      credentials: cookies ? "include" : "omit"
    });
  } catch (e) {
    throw new ApiError({ code: "NETWORK", message: e.message }, 0);
  }

  return settle(res.status, await res.text(), res.headers.get("set-auth-token"), token);
}

function settle(status, text, issued, token) {
  if (issued) setToken(issued);
  let parsed = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch (e) {
      parsed = { message: text.slice(0, 300) };
    }
  }

  if (status === 401 && token && !issued) setToken(null);
  if (status < 200 || status > 299) throw new ApiError(parsed, status);
  return parsed;
}

// fetch() cannot report upload progress, so files go through XMLHttpRequest with the same auth and error shape.
function sendFile(url, file, { onProgress, signal } = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    xhr.setRequestHeader("Accept", "application/json");
    const token = getToken();
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    if (onProgress) xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onerror = () => reject(new ApiError({ code: "NETWORK", message: "Network error" }, 0));
    xhr.onabort = () => reject(new ApiError({ code: "ABORTED", message: "Upload cancelled" }, 0));
    xhr.onload = () => {
      try {
        resolve(settle(xhr.status, xhr.responseText, xhr.getResponseHeader("set-auth-token"), token));
      } catch (e) {
        reject(e);
      }
    };
    signal?.addEventListener("abort", () => xhr.abort(), { once: true });
    const body = new FormData();
    body.append("file", file, file.name);
    xhr.send(body);
  });
}

const portal = (method, path, body) => call(`${PORTAL_BASE}${path}`, { method, body }).then(unwrap);
const auth = (path, body, opts = {}) => call(`${AUTH_BASE}${path}`, { method: "POST", body, ...opts });
const enc = encodeURIComponent;

export const authApi = {
  signInEmail: (email, password) => auth("/sign-in/email", { email, password }, { cookies: true }),
  signUpEmail: (payload) => auth("/sign-up/email", payload),
  signOut: () => auth("/sign-out", {}, { cookies: true }),
  sendVerificationOtp: (email) =>
    auth("/email-otp/send-verification-otp", { email, type: "email-verification" }),
  verifyEmail: (email, otp) => auth("/email-otp/verify-email", { email, otp }),
  enableTwoFactor: (password) => auth("/two-factor/enable", { password }),
  verifyTotp: (code, trustDevice = false) =>
    auth("/two-factor/verify-totp", { code, trustDevice }, { cookies: true }),
  verifyBackupCode: (code) => auth("/two-factor/verify-backup-code", { code }, { cookies: true })
};

export const api = {
  signupMode: () => portal("GET", "/signup-mode"),
  registerProvider: (payload) => portal("POST", "/auth/register-provider", payload),
  acceptInvite: (payload) => portal("POST", "/auth/accept-invite", payload),

  me: () => portal("GET", "/me"),
  provider: () => portal("GET", "/provider"),
  updateProvider: (payload) => portal("PATCH", "/provider", payload),

  apps: () => portal("GET", "/apps"),
  createApp: (payload) => portal("POST", "/apps", payload),
  app: (id) => portal("GET", `/apps/${enc(id)}`),
  updateApp: (id, payload) => portal("PATCH", `/apps/${enc(id)}`, payload),

  versions: (id) => portal("GET", `/apps/${enc(id)}/versions`),
  createVersion: (id, payload) => portal("POST", `/apps/${enc(id)}/versions`, payload),
  submitVersion: (id, vid) => portal("POST", `/apps/${enc(id)}/versions/${enc(vid)}/submit`),
  review: (id, vid) => portal("GET", `/apps/${enc(id)}/versions/${enc(vid)}/review`),

  listing: (id, locale) => portal("GET", `/apps/${enc(id)}/listing?locale=${enc(locale)}`),
  saveListing: (id, payload) => portal("PUT", `/apps/${enc(id)}/listing`, payload),
  // kind: "icon" | "screenshot"; resolves to { url, width, height, ... } — store url like a pasted one.
  uploadMedia: (id, kind, file, opts) =>
    sendFile(`${PORTAL_BASE}/apps/${enc(id)}/media?kind=${enc(kind)}`, file, opts).then(unwrap),

  distribution: (id) => portal("GET", `/apps/${enc(id)}/distribution`),
  saveDistribution: (id, payload) => portal("PUT", `/apps/${enc(id)}/distribution`, payload),
  installations: () => portal("GET", "/installations"),

  credentials: (id) => portal("GET", `/apps/${enc(id)}/credentials`),
  createCredential: (id, payload) => portal("POST", `/apps/${enc(id)}/credentials`, payload),
  rotateCredential: (id, cid) => portal("POST", `/apps/${enc(id)}/credentials/${enc(cid)}/rotate`),
  revokeCredential: (id, cid) => portal("POST", `/apps/${enc(id)}/credentials/${enc(cid)}/revoke`),

  testers: (id) => portal("GET", `/apps/${enc(id)}/testers`),
  inviteTester: (id, payload) => portal("POST", `/apps/${enc(id)}/testers`, payload),
  revokeTester: (id, tid) => portal("DELETE", `/apps/${enc(id)}/testers/${enc(tid)}`),

  usage: (id, days = 7) => portal("GET", `/apps/${enc(id)}/usage?days=${enc(days)}`),
  installs: (id) => portal("GET", `/apps/${enc(id)}/installs`),

  team: () => portal("GET", "/team"),
  inviteMember: (payload) => portal("POST", "/team/invites", payload),
  removeMember: (userId) => portal("DELETE", `/team/${enc(userId)}`),

  notices: () => portal("GET", "/notices"),
  scopes: () => portal("GET", "/scopes"),
  webhookTopics: () => portal("GET", "/webhook-topics")
};

export default api;
